import { Profile } from '@garmin/fitsdk'

import type { Activity, FitMessage } from '../model/activity.js'
import { mesgNumByMessagesKey } from '../model/profile-lookup.js'
import type { Operation, OperationResult } from './operations.js'

/**
 * `editField` — set or remove one FIT Profile field on one message
 * instance (`add-field-editing` change, `activity-editing` spec delta).
 * Backs the UI's "All messages" raw-data view.
 *
 * Values follow the SDK decoder's conventions, so an edited message looks
 * exactly like what re-decoding the exported file yields:
 *
 * - numeric fields hold real-world units (scale/offset already applied);
 *   a value is quantized to the field's resolution the way the encoder
 *   rounds it, and must land inside the base type's range (the invalid
 *   sentinel excluded).
 * - Profile-typed fields (`sport`, `manufacturer`, ...) hold the type's
 *   value name, or the raw integer when it has no name. Either is
 *   accepted; a raw integer with a name is stored as the name.
 * - `dateTime` fields hold a `Date` (whole seconds).
 * - `bool` fields hold 0/1 (the SDK decodes them as numbers and cannot
 *   encode JS booleans); `true`/`false` are accepted and stored as 1/0.
 *
 * Fields the profile links together are kept consistent, because the
 * decoder would otherwise silently undo the edit on re-import:
 *
 * - component pairs such as `speed` -> `enhancedSpeed`: the decoder
 *   re-derives the enhanced field from the legacy one, so editing either
 *   updates both (the legacy field is removed when it cannot represent
 *   the value exactly), and removing either removes both.
 * - sub-fields such as `product` -> `garminProduct` (selected by
 *   `manufacturer`) or session `avgCadence` -> `avgRunningCadence`
 *   (selected by `sport`): sub-field keys are re-derived whenever their
 *   main field or a selecting field changes. A sub-field can be edited
 *   by name only while it is the active interpretation of its main field.
 *
 * Not editable (see `describeEditableField` for the per-field reason):
 * developer fields and unknown fields (preserved raw, out of scope for
 * v1), developer-data-id / field-description messages, every
 * `timestamp` field (it orders the file and closes laps/sessions; use
 * shift time), `file_id.type`, array and 64-bit fields, packed
 * multi-component fields, and rolling counters the decoder accumulates.
 *
 * Laps/sessions are NOT recalculated after an edit: an edited summary
 * field is exactly what the user typed. FIT self-validation still checks
 * lap/session timing and distance against the records at export and
 * reports any mismatch rather than hiding it.
 */

export type EditFieldValue = number | string | boolean | Date | null
export type EditFieldOperation = Extract<Operation, { type: 'editField' }>

export interface EditableFieldDescription {
  editable: boolean
  /** Why the field can't be edited (only when `editable` is false). */
  reason?: string
  kind: 'number' | 'enum' | 'string' | 'date'
  units?: string
  /**
   * Inclusive bounds. `number`: real-world units. `enum`: the raw integer
   * range also accepted besides `enumValues`. `date`: epoch milliseconds.
   */
  min?: number
  max?: number
  /** `number` only: the smallest step the field can store (1 / scale). */
  resolution?: number
  /** `number` only: the value must be a whole number. */
  integer?: boolean
  /** `enum` only: valid value names from the FIT Profile type. */
  enumValues?: string[]
  /** `string` only: maximum UTF-8 byte length. */
  maxBytes?: number
  /** Set when `field` is a sub-field: the main field it re-interprets. */
  subFieldOf?: string
}

// ---------------------------------------------------------------------------
// Profile access
// ---------------------------------------------------------------------------

type Scale = number | number[]

interface ProfileSubField {
  name: string
  type: string
  baseType: string
  scale: Scale
  offset: Scale
  units: string | string[]
  components: number[]
  hasComponents: boolean
  map: Array<{ name: string; value: number }>
}

interface ProfileField {
  num: number
  name: string
  type: string
  baseType: string
  array: boolean
  scale: Scale
  offset: Scale
  units: string | string[]
  bits: number[]
  components: number[]
  isAccumulated: boolean
  subFields: ProfileSubField[]
}

interface ProfileMessage {
  num: number
  name: string
  messagesKey: string
  fields: Record<string, ProfileField>
}

/** The subset of a field definition value conversion needs (fields and sub-fields alike). */
type ValueDef = Pick<ProfileField, 'name' | 'type' | 'baseType' | 'scale' | 'offset' | 'units'>

const PROFILE_MESSAGES = Profile.messages as unknown as Record<string, ProfileMessage>
const PROFILE_TYPES = Profile.types as unknown as Record<string, Record<string, string>>

const FIT_EPOCH_MS = 631065600000
/** FIT `date_time` values below this are device-relative, not absolute dates. */
const MIN_ABSOLUTE_DATE_TIME = 0x10000000
const MAX_STRING_BYTES = 254 // 255-byte field size minus the NUL terminator
const TIMESTAMP_FIELD_NUM = 253

const UNEDITABLE_MESSAGES: Record<string, string> = {
  developerDataIdMesgs:
    'developer data ID messages define how developer fields are encoded; editing them would break those fields',
  fieldDescriptionMesgs:
    'field description messages define how developer fields are encoded; editing them would break those fields',
}

/** [min, max] raw value per base type, the invalid sentinel excluded. */
const BASE_TYPE_RAW_RANGE: Record<string, [number, number]> = {
  enum: [0, 0xfe],
  sint8: [-0x80, 0x7e],
  uint8: [0, 0xfe],
  sint16: [-0x8000, 0x7ffe],
  uint16: [0, 0xfffe],
  sint32: [-0x80000000, 0x7ffffffe],
  uint32: [0, 0xfffffffe],
  uint8z: [1, 0xff],
  uint16z: [1, 0xffff],
  uint32z: [1, 0xffffffff],
  byte: [0, 0xfe],
  float32: [-3.4028234663852886e38, 3.4028234663852886e38],
  float64: [-Number.MAX_VALUE, Number.MAX_VALUE],
}
const NUMERIC_TYPES = new Set(Object.keys(BASE_TYPE_RAW_RANGE).filter((t) => t !== 'enum'))
const FLOAT_TYPES = new Set(['float32', 'float64'])
const BIGINT_TYPES = new Set(['sint64', 'uint64', 'uint64z'])

function first(value: Scale | undefined, fallback: number): number {
  if (Array.isArray(value)) return value[0] ?? fallback
  return value ?? fallback
}

function unitsOf(def: ValueDef): string | undefined {
  const units = Array.isArray(def.units) ? def.units[0] : def.units
  return units != null && units !== '' ? units : undefined
}

function messageProfile(messagesKey: string): ProfileMessage | undefined {
  const num = mesgNumByMessagesKey().get(messagesKey)
  return num == null ? undefined : PROFILE_MESSAGES[String(num)]
}

function fieldByName(message: ProfileMessage, name: string): ProfileField | undefined {
  return Object.values(message.fields).find((f) => f.name === name)
}

function fieldByNum(message: ProfileMessage, num: number): ProfileField | undefined {
  return message.fields[String(num)]
}

interface ResolvedField {
  main: ProfileField
  sub?: ProfileSubField
}

function resolveField(message: ProfileMessage, name: string): ResolvedField | undefined {
  const main = fieldByName(message, name)
  if (main != null) return { main }
  for (const field of Object.values(message.fields)) {
    const sub = field.subFields.find((s) => s.name === name)
    if (sub != null) return { main: field, sub }
  }
  return undefined
}

/** A single full-width component into a plain numeric field: a value maps 1:1 onto its target. */
function simpleComponentTarget(
  message: ProfileMessage,
  field: ProfileField,
): ProfileField | undefined {
  if (field.components.length !== 1) return undefined
  const target = fieldByNum(message, field.components[0] as number)
  if (target == null || target.isAccumulated) return undefined
  if (target.subFields.length > 0 || target.components.length > 0) return undefined
  if (!NUMERIC_TYPES.has(target.type)) return undefined
  const range = BASE_TYPE_RAW_RANGE[field.baseType]
  const fullBits = { uint8: 8, sint8: 8, uint16: 16, sint16: 16, uint32: 32, sint32: 32 }[
    field.baseType
  ]
  if (range == null || fullBits == null || field.bits[0] !== fullBits) return undefined
  return target
}

/** Fields of `message` whose (single, simple) component expands into `target`. */
function simpleComponentSources(message: ProfileMessage, target: ProfileField): ProfileField[] {
  return Object.values(message.fields).filter(
    (f) => simpleComponentTarget(message, f)?.num === target.num,
  )
}

// ---------------------------------------------------------------------------
// describeEditableField
// ---------------------------------------------------------------------------

function notEditable(reason: string, kind: EditableFieldDescription['kind'] = 'number') {
  return { editable: false, reason, kind } satisfies EditableFieldDescription
}

function describeValueDef(def: ValueDef): EditableFieldDescription {
  const units = unitsOf(def)
  const withUnits = units != null ? { units } : {}
  if (def.type === 'string') {
    return { editable: true, kind: 'string', maxBytes: MAX_STRING_BYTES }
  }
  if (def.type === 'dateTime') {
    return {
      editable: true,
      kind: 'date',
      min: FIT_EPOCH_MS + MIN_ABSOLUTE_DATE_TIME * 1000,
      max: FIT_EPOCH_MS + 0xfffffffe * 1000,
    }
  }
  if (def.type === 'bool') {
    return { editable: true, kind: 'number', min: 0, max: 1, integer: true, resolution: 1 }
  }
  if (BIGINT_TYPES.has(def.type)) {
    return notEditable('64-bit fields are not editable in this version')
  }
  if (NUMERIC_TYPES.has(def.type)) {
    const [rawMin, rawMax] = BASE_TYPE_RAW_RANGE[def.type] as [number, number]
    const scale = first(def.scale, 1)
    const offset = first(def.offset, 0)
    const isFloat = FLOAT_TYPES.has(def.type)
    const integer = !isFloat && scale === 1 && offset === 0
    return {
      editable: true,
      kind: 'number',
      ...withUnits,
      min: rawMin / scale - offset,
      max: rawMax / scale - offset,
      ...(isFloat ? {} : { resolution: 1 / scale }),
      integer,
    }
  }
  const values = PROFILE_TYPES[def.type]
  if (values != null) {
    const [rawMin, rawMax] = BASE_TYPE_RAW_RANGE[def.baseType] ?? [0, 0]
    return {
      editable: true,
      kind: 'enum',
      ...withUnits,
      enumValues: Object.values(values),
      min: rawMin,
      max: rawMax,
    }
  }
  return notEditable(`the FIT Profile type "${def.type}" is not supported for editing`)
}

/**
 * Whether `field` of message type `messagesKey` (SDK names, e.g.
 * `'sessionMesgs'`, `'totalDistance'`) can be edited with `editField`,
 * and how its value is validated — so a UI can render the right input
 * and reject bad input before applying. Instance-specific checks (index
 * range, whether a sub-field is currently active, packed source fields
 * present on that instance) happen only in `applyOperation`.
 */
export function describeEditableField(
  messagesKey: string,
  field: string,
): EditableFieldDescription {
  const message = messageProfile(messagesKey)
  if (message == null) return notEditable(`"${messagesKey}" is not a FIT Profile message type`)
  const messageReason = UNEDITABLE_MESSAGES[messagesKey]
  if (messageReason != null) return notEditable(messageReason)
  if (field === 'developerFields') {
    return notEditable(
      'developer fields are preserved exactly as recorded and are not editable in this version',
    )
  }
  const resolved = resolveField(message, field)
  if (resolved == null) {
    return notEditable(
      `"${field}" is not a FIT Profile field of ${message.name}; unknown fields are preserved exactly as recorded and are not editable in this version`,
    )
  }
  const { main, sub } = resolved
  if (main.num === TIMESTAMP_FIELD_NUM) {
    return notEditable(
      'message timestamps order the file and mark where laps and sessions end; use Shift time to move timestamps',
      'date',
    )
  }
  if (messagesKey === 'fileIdMesgs' && main.name === 'type') {
    return notEditable(
      'the file type marks this as an activity file; changing it would make it a different kind of FIT file',
      'enum',
    )
  }
  if (main.array && main.type !== 'string') {
    return notEditable('array fields are not editable in this version')
  }
  if (main.components.length > 1) {
    return notEditable(
      'this field packs several values into one; edit the fields it expands into instead',
    )
  }
  if (main.subFields.some((s) => s.hasComponents)) {
    return notEditable(
      'some interpretations of this field pack several values into one; it is not editable in this version',
    )
  }
  if (main.components.length === 1 && simpleComponentTarget(message, main) == null) {
    const target = fieldByNum(message, main.components[0] as number)
    return notEditable(
      `the decoder derives ${target?.name ?? 'another field'} from this field (as a rolling counter or packed value), so an edit here would not round-trip`,
    )
  }
  const description = describeValueDef(sub ?? main)
  return sub != null ? { ...description, subFieldOf: main.name } : description
}

// ---------------------------------------------------------------------------
// Value conversion
// ---------------------------------------------------------------------------

function fail(op: EditFieldOperation, reason: string): never {
  throw new Error(`editField ${op.messagesKey} #${op.index + 1} ${op.field}: ${reason}.`)
}

interface Converted {
  /** Value to store (decoder representation). */
  value: unknown
  /** Raw integer/float as written to the file. */
  raw: number
}

function toRawFromStored(def: ValueDef, value: unknown): number | undefined {
  if (value instanceof Date) return Math.round((value.getTime() - FIT_EPOCH_MS) / 1000)
  if (typeof value === 'string') {
    const values = PROFILE_TYPES[def.type]
    if (values == null) return undefined
    const entry = Object.entries(values).find(([, name]) => name === value)
    return entry == null ? undefined : Number(entry[0])
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  if (!NUMERIC_TYPES.has(def.type)) return value
  const scaled = (value + first(def.offset, 0)) * first(def.scale, 1)
  return FLOAT_TYPES.has(def.type) ? scaled : Math.round(scaled)
}

/** Exactly what the SDK decoder would produce for `raw` (value / scale - offset, type names, dates). */
function fromRaw(def: ValueDef, raw: number): unknown {
  if (def.type === 'dateTime') return new Date(raw * 1000 + FIT_EPOCH_MS)
  if (NUMERIC_TYPES.has(def.type)) {
    if (def.type === 'float32') return Math.fround(raw)
    return raw / first(def.scale, 1) - first(def.offset, 0)
  }
  const values = PROFILE_TYPES[def.type]
  return values?.[String(raw)] ?? raw
}

function convertValue(op: EditFieldOperation, def: ValueDef, value: unknown): Converted {
  const describe = describeValueDef(def)
  switch (describe.kind) {
    case 'string': {
      if (typeof value !== 'string') fail(op, 'expected a text value')
      if (value === '') fail(op, 'an empty text value cannot be stored; remove the field instead')
      if (value.includes('\0')) fail(op, 'text cannot contain a NUL character')
      const bytes = new TextEncoder().encode(value).length
      if (bytes > MAX_STRING_BYTES) {
        fail(op, `text is ${bytes} UTF-8 bytes; the maximum is ${MAX_STRING_BYTES}`)
      }
      return { value, raw: Number.NaN }
    }
    case 'date': {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        fail(op, 'expected a valid date')
      }
      if (value.getTime() % 1000 !== 0) fail(op, 'FIT dates are whole seconds')
      const raw = (value.getTime() - FIT_EPOCH_MS) / 1000
      if (raw < MIN_ABSOLUTE_DATE_TIME || raw > 0xfffffffe) {
        fail(
          op,
          `date must be between ${new Date(describe.min as number).toISOString()} and ${new Date(describe.max as number).toISOString()}`,
        )
      }
      return { value: new Date(value.getTime()), raw }
    }
    case 'enum': {
      const values = PROFILE_TYPES[def.type] ?? {}
      if (typeof value === 'string') {
        const raw = toRawFromStored(def, value)
        if (raw == null) {
          fail(op, `"${value}" is not a valid ${def.type} value`)
        }
        return { value, raw }
      }
      if (typeof value === 'number') {
        if (
          !Number.isInteger(value) ||
          value < (describe.min ?? 0) ||
          value > (describe.max ?? 0)
        ) {
          fail(
            op,
            `a raw ${def.type} value must be a whole number from ${describe.min} to ${describe.max}`,
          )
        }
        return { value: values[String(value)] ?? value, raw: value }
      }
      return fail(op, `expected a ${def.type} value name`)
    }
    case 'number': {
      let n = value
      if (def.type === 'bool' && typeof value === 'boolean') n = value ? 1 : 0
      if (typeof n !== 'number' || !Number.isFinite(n)) fail(op, 'expected a finite number')
      if (describe.integer === true && !Number.isInteger(n)) fail(op, 'expected a whole number')
      const raw = toRawFromStored(def, n) as number
      const [rawMin, rawMax] =
        def.type === 'bool' ? [0, 1] : (BASE_TYPE_RAW_RANGE[def.type] as [number, number])
      if (raw < rawMin || raw > rawMax) {
        fail(op, `value must be from ${describe.min} to ${describe.max}`)
      }
      return { value: def.type === 'bool' ? raw : fromRaw(def, raw), raw }
    }
  }
}

// ---------------------------------------------------------------------------
// Activity access
// ---------------------------------------------------------------------------

const NAMED_ARRAYS = {
  recordMesgs: 'records',
  lapMesgs: 'laps',
  sessionMesgs: 'sessions',
  eventMesgs: 'events',
} as const
const NAMED_SINGLES = { fileIdMesgs: 'fileId', activityMesgs: 'activitySummary' } as const

function instancesOf(activity: Activity, messagesKey: string): FitMessage[] {
  if (messagesKey in NAMED_ARRAYS) {
    return activity[NAMED_ARRAYS[messagesKey as keyof typeof NAMED_ARRAYS]]
  }
  if (messagesKey in NAMED_SINGLES) {
    const single = activity[NAMED_SINGLES[messagesKey as keyof typeof NAMED_SINGLES]]
    return single == null ? [] : [single]
  }
  return activity.otherMessages[messagesKey] ?? []
}

function withInstance(
  activity: Activity,
  messagesKey: string,
  index: number,
  instance: FitMessage,
): Activity {
  const replace = <T extends FitMessage>(list: T[]): T[] =>
    list.map((m, i) => (i === index ? (instance as T) : m))
  if (messagesKey in NAMED_ARRAYS) {
    const key = NAMED_ARRAYS[messagesKey as keyof typeof NAMED_ARRAYS]
    return { ...activity, [key]: replace(activity[key]) }
  }
  if (messagesKey in NAMED_SINGLES) {
    return { ...activity, [NAMED_SINGLES[messagesKey as keyof typeof NAMED_SINGLES]]: instance }
  }
  return {
    ...activity,
    otherMessages: {
      ...activity.otherMessages,
      [messagesKey]: replace(activity.otherMessages[messagesKey] ?? []),
    },
  }
}

// ---------------------------------------------------------------------------
// Linked fields
// ---------------------------------------------------------------------------

function omit(instance: FitMessage, key: string): FitMessage {
  if (!(key in instance)) return instance
  const { [key]: _omitted, ...rest } = instance
  return rest
}

function subFieldActive(
  message: ProfileMessage,
  instance: FitMessage,
  sub: ProfileSubField,
): boolean {
  return sub.map.some((m) => {
    const ref = fieldByName(message, m.name)
    if (ref == null) return false
    return toRawFromStored(ref, instance[m.name]) === m.value
  })
}

/**
 * Re-derives every sub-field key of `main` from its current value and the
 * current selecting fields — mirroring the SDK decoder's sub-field
 * expansion, so the in-memory message matches a re-decode.
 */
function rederiveSubFields(
  message: ProfileMessage,
  instance: FitMessage,
  main: ProfileField,
): FitMessage {
  let result = instance
  for (const sub of main.subFields) result = omit(result, sub.name)
  const raw = toRawFromStored(main, result[main.name])
  if (raw == null) return result
  for (const sub of main.subFields) {
    if (subFieldActive(message, result, sub)) result = { ...result, [sub.name]: fromRaw(sub, raw) }
  }
  return result
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null) return '(not set)'
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

/** Lap/session/activity fields FIT self-validation compares against the records. */
const VALIDATED_FIELDS: Record<string, string[]> = {
  lapMesgs: ['startTime', 'totalElapsedTime', 'totalTimerTime', 'totalDistance'],
  sessionMesgs: ['startTime', 'totalElapsedTime', 'totalTimerTime', 'totalDistance'],
  activityMesgs: ['numSessions'],
}

// ---------------------------------------------------------------------------
// applyEditField
// ---------------------------------------------------------------------------

export function applyEditField(activity: Activity, op: EditFieldOperation): OperationResult {
  if (typeof op.messagesKey !== 'string' || typeof op.field !== 'string') {
    throw new Error('editField: messagesKey and field must be strings.')
  }
  const description = describeEditableField(op.messagesKey, op.field)
  if (!description.editable) fail(op, description.reason ?? 'not editable')
  const message = messageProfile(op.messagesKey) as ProfileMessage
  const { main, sub } = resolveField(message, op.field) as ResolvedField

  const instances = instancesOf(activity, op.messagesKey)
  if (!Number.isInteger(op.index) || op.index < 0 || op.index >= instances.length) {
    fail(op, `there is no such message (the activity has ${instances.length})`)
  }
  const before = instances[op.index] as FitMessage
  if (Array.isArray(before[op.field])) fail(op, 'array values are not editable in this version')

  // A packed/accumulating source present on this instance would overwrite
  // the edited field on re-decode.
  for (const source of Object.values(message.fields)) {
    if (before[source.name] == null || !source.components.includes(main.num)) continue
    if (simpleComponentTarget(message, source)?.num === main.num) continue
    fail(
      op,
      `this message also carries ${source.name}, which the decoder expands into ${main.name}, so the edit would not survive export`,
    )
  }
  if (sub != null && !subFieldActive(message, before, sub)) {
    fail(
      op,
      `${sub.name} is not the active interpretation of ${main.name} for this message (it depends on ${sub.map.map((m) => m.name).join('/')})`,
    )
  }

  const oldValue = before[op.field]
  const notes: string[] = []
  let after: FitMessage

  if (op.value === null) {
    if (before[main.name] == null) fail(op, 'the field is not set')
    after = omit(before, main.name)
    for (const s of main.subFields) after = omit(after, s.name)
    const target = simpleComponentTarget(message, main)
    if (target != null && after[target.name] != null) {
      after = omit(after, target.name)
      notes.push(`also removed ${target.name}, which the FIT profile derives from ${main.name}`)
    }
    for (const source of simpleComponentSources(message, main)) {
      if (after[source.name] == null) continue
      after = omit(after, source.name)
      notes.push(
        `also removed ${source.name}, from which the FIT profile would otherwise re-derive ${main.name}`,
      )
    }
    const remaining = Object.keys(after).filter(
      (k) => k !== 'developerFields' && fieldByName(message, k) != null,
    )
    if (remaining.length === 0) {
      fail(op, 'a FIT message must keep at least one field; it would have none left')
    }
  } else {
    const converted = convertValue(op, sub ?? main, op.value)
    const mainValue = sub != null ? fromRaw(main, converted.raw) : converted.value
    after = { ...before, [main.name]: mainValue }

    const target = simpleComponentTarget(message, main)
    if (target != null) {
      // The decoder always re-derives the target from this field.
      if (after[target.name] !== mainValue) {
        after = { ...after, [target.name]: mainValue }
        notes.push(`also set ${target.name}, which the FIT profile derives from ${main.name}`)
      }
    }
    for (const source of simpleComponentSources(message, main)) {
      if (after[source.name] == null) continue
      const raw = toRawFromStored(source, mainValue)
      const [rawMin, rawMax] = BASE_TYPE_RAW_RANGE[source.type] ?? [0, -1]
      const exact =
        raw != null && raw >= rawMin && raw <= rawMax && fromRaw(source, raw) === mainValue
      if (exact) {
        if (after[source.name] !== mainValue) {
          after = { ...after, [source.name]: mainValue }
          notes.push(`also set ${source.name} to keep it consistent with ${main.name}`)
        }
      } else {
        after = omit(after, source.name)
        notes.push(
          `removed ${source.name}: it cannot store ${formatValue(mainValue)} exactly and would overwrite ${main.name} on re-import`,
        )
      }
    }
  }

  // Sub-fields of the edited field, and sub-fields this field selects.
  if (main.subFields.length > 0) after = rederiveSubFields(message, after, main)
  for (const other of Object.values(message.fields)) {
    if (other.num === main.num || other.subFields.length === 0) continue
    if (!other.subFields.some((s) => s.map.some((m) => m.name === main.name))) continue
    const rederived = rederiveSubFields(message, after, other)
    const changed = other.subFields.some((s) => rederived[s.name] !== after[s.name])
    if (changed) notes.push(`re-derived ${other.name}'s ${main.name}-dependent interpretation`)
    after = rederived
  }

  const newValue = after[op.field]
  let text = `Changed ${message.name} #${op.index + 1} ${op.field} from ${formatValue(oldValue)} to ${formatValue(newValue)}`
  if (notes.length > 0) text += `; ${notes.join('; ')}`
  text += '.'
  if (VALIDATED_FIELDS[op.messagesKey]?.includes(main.name) === true) {
    text +=
      ' This field is not recalculated; FIT export validation compares it with the records and reports it if they disagree.'
  }

  return {
    activity: withInstance(activity, op.messagesKey, op.index, after),
    warnings: [{ message: text }],
  }
}
